import type { Floor } from '@/world/builder';
import type { Mansion } from '@/world/mansion';
import type { GroundItem } from './entities';
import { boxFree, tileCenter } from './physics';

/** Keep the small visual offset only when the item still rests on clear floor. */
export function dropPoint(f: Floor, from: { x: number; y: number }): { x: number; y: number } {
  const y = from.y + 4;
  return { x: from.x, y: boxFree(f, from.x, y, d => d.open, 2, 2) ? y : from.y };
}

/** Repair only old loose-item placements whose originating action is identifiable. */
export function repairLooseItems(mansion: Mansion, items: GroundItem[]): number {
  let repaired = 0;
  for (const it of items) {
    if (it.bag) continue;
    const f = mansion.floors.get(it.floor);
    if (!f) continue;
    const free = (x: number, y: number) => boxFree(f, x, y, d => d.open, 2, 2);
    if (it.id.startsWith('drop:')) {
      // Older drops always added four pixels to the actor's valid feet position.
      if (!free(it.x, it.y) && free(it.x, it.y - 4)) { it.y -= 4; repaired++; }
      continue;
    }
    if (!it.id.startsWith('loot:')) continue;
    const prop = f.props.find(p => p.id === it.id.slice(5));
    if (!prop || free(it.x, it.y)) continue;
    const room = f.roomById.get(prop.room);
    if (!room) continue;
    let best: { x: number; y: number; distance: number } | null = null;
    for (let ty = room.y; ty < room.y + room.h; ty++) for (let tx = room.x; tx < room.x + room.w; tx++) {
      const x = tileCenter(tx), y = tileCenter(ty);
      if (!boxFree(f, x, y, d => d.open)) continue;
      const distance = (x - it.x) ** 2 + (y - it.y) ** 2;
      if (!best || distance < best.distance) best = { x, y, distance };
    }
    if (best) { it.x = best.x; it.y = best.y; repaired++; }
  }
  return repaired;
}
